import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Spinner from "../components/Spinner";
import { PRODUCT_CONDITIONS } from "../constants/productCondition";

const PRODUCT_API_BASE = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const INVENTORY_API_BASE = process.env.REACT_APP_INVENTORY_SERVICE_URL;

export default function SellerEditProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [product, setProduct] = useState(null);
  const [inventory, setInventory] = useState(null);

  const IMAGE_BASE = "https://auth.afrogebeya.com/uploads/products/";

  const [existingImages, setExistingImages] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [removedImages, setRemovedImages] = useState([]);

  const [newImages, setNewImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);

  const [form, setForm] = useState({
    productName: "",
    description: "",
    sku: "",
    price: "",
    currency: "ETB",
    productCondition: "NEW",
    productStatus: "ACTIVE",
    brand: "",
    modelNumber: "",
  });

  const [stock, setStock] = useState({
    sellableQuantity: 0,
  });

  // ===============================
  // Fetch Product + Inventory
  // ===============================
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const productRes = await fetch(`${PRODUCT_API_BASE}/products/${id}`);

        if (!productRes.ok) throw new Error("Failed to fetch product");

        const productData = await productRes.json();
        setProduct(productData);

        setForm({
          productName: productData.productName || "",
          description: productData.description || "",
          sku: productData.sku || "",
          price: productData.price || "",
          currency: productData.currency || "ETB",
          productStatus: productData.productStatus || "ACTIVE",
          productCondition: productData.condition || "NEW",
          brand: productData.brand || "",
          modelNumber: productData.modelNumber || "",
        });

        // image handling
        setExistingImages(productData.images || []);
        setMainImage(productData.mainImage?.id || null);

        const inventoryRes = await fetch(
          `${INVENTORY_API_BASE}/inventories/stock?productId=${id}`,
        );

        if (inventoryRes.ok) {
          const inventoryData = await inventoryRes.json();
          setInventory(inventoryData);
          setStock({
            sellableQuantity: inventoryData.sellableQuantity || 0,
          });
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load product data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  // ===============================
  // Handle Changes
  // ===============================
  const handleChange = (e) => {
    const { name, value } = e.target;

    const numericFields = ["price"];

    if (numericFields.includes(name)) {
      if (value === "") {
        setForm((prev) => ({ ...prev, [name]: "" }));
        return;
      }

      const num = Number(value);
      if (isNaN(num) || num < 0) return;

      setForm((prev) => ({ ...prev, [name]: num }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleStockChange = (e) => {
    const value = Number(e.target.value);
    if (isNaN(value) || value < 0) return;

    setStock({ sellableQuantity: value });
  };

  const handleRemoveExistingImage = (imageObj) => {
    // remove from existing images
    setExistingImages((prev) => prev.filter((img) => img.id !== imageObj.id));

    // track removed IDs
    setRemovedImages((prev) => [...prev, imageObj.id]);

    // if the removed image was the main image, pick a fallback
    if (mainImage === imageObj.id) {
      setExistingImages((prev) => {
        const remaining = prev.filter((img) => img.id !== imageObj.id);
        if (remaining.length > 0) {
          setMainImage(remaining[0].id); // choose first remaining
        } else {
          setMainImage(null); // no images left
        }
        return remaining;
      });
    }
  };

  // Add new images
  const handleNewImages = (e) => {
    const files = Array.from(e.target.files);

    setNewImages((prev) => [...prev, ...files]);

    const previews = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));

    setPreviewImages((prev) => [...prev, ...previews]);
  };

  const handleRemoveNewImage = (index) => {
    URL.revokeObjectURL(previewImages[index].url);

    setNewImages((prev) => prev.filter((_, i) => i !== index));
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  // ===============================
  // Save Changes
  // ===============================
  const handleSave = async () => {
    try {
      setSaving(true);

      // -----------------------------
      // 1 Update product fields
      // -----------------------------
      const productUpdateRequest = {
        productName: form.productName,
        description: form.description,
        sku: form.sku,
        price: form.price,
        currency: form.currency,
        productCondition: form.productCondition,
        productStatus: form.productStatus,
        brand: form.brand,
        modelNumber: form.modelNumber,
      };

      const productRes = await fetch(
        `${PRODUCT_API_BASE}/products/seller/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(productUpdateRequest),
        },
      );

      if (!productRes.ok) {
        throw new Error("Failed to update product");
      }

      // -----------------------------
      // 2 Upload new images
      // -----------------------------
      if (newImages.length > 0) {
        const formData = new FormData();

        newImages.forEach((file) => {
          formData.append("images", file);
        });

        const imageUploadRes = await fetch(
          `${PRODUCT_API_BASE}/products/${id}/images`,
          {
            method: "POST",
            body: formData,
          },
        );

        if (!imageUploadRes.ok) {
          throw new Error("Failed to upload images");
        }
      }

      // -----------------------------
      // 3 Delete removed images
      // -----------------------------

      await Promise.all(
        removedImages.map(async (imageId) => {
          const res = await fetch(
            `${PRODUCT_API_BASE}/products/images/${imageId}`,
            { method: "DELETE" },
          );

          if (!res.ok) {
            throw new Error(`Failed deleting image ${imageId}`);
          }
        }),
      );
      // -----------------------------
      // 4 Update main image
      // -----------------------------
      if (mainImage != null) {
        const mainRes = await fetch(
          `${PRODUCT_API_BASE}/products/${id}/main-image?imageId=${mainImage}`,
          {
            method: "PUT",
          },
        );

        if (!mainRes.ok) {
          throw new Error("Failed to update main image");
        }
      }

      // -----------------------------
      // 5 Update inventory
      // -----------------------------
      const inventoryRes = await fetch(
        `${INVENTORY_API_BASE}/inventories/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(stock),
        },
      );

      if (!inventoryRes.ok) {
        throw new Error("Failed to update inventory");
      }

      navigate("/dashboard/catalog");
    } catch (err) {
      console.error(err);
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="container py-4">
      <h4 className="mb-4 fw-semibold">Edit Product</h4>

      <div className="card shadow-sm p-4">
        {/* Product Name */}
        <div className="mb-3">
          <label className="form-label">Product Name</label>
          <input
            className="form-control"
            name="productName"
            value={form.productName}
            onChange={handleChange}
          />
        </div>

        {/* Description */}
        <div className="mb-3">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows={4}
            name="description"
            value={form.description}
            onChange={handleChange}
          />
        </div>

        {/* SKU */}
        <div className="mb-3">
          <label className="form-label">SKU</label>
          <input
            className="form-control"
            name="sku"
            value={form.sku}
            onChange={handleChange}
          />
        </div>

        {/* Price */}
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Price</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="form-control"
              name="price"
              value={form.price}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Currency</label>
            <select
              className="form-select"
              name="currency"
              value={form.currency}
              onChange={handleChange}
            >
              <option value="ETB">ETB</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>

        {/* Stock */}
        <div className="mb-3">
          <label className="form-label">Sellable Quantity</label>
          <input
            type="number"
            min="0"
            className="form-control"
            value={stock.sellableQuantity}
            onChange={handleStockChange}
          />
        </div>

        {/* Status */}
        <div className="mb-3">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            name="productStatus"
            value={form.productStatus}
            onChange={handleChange}
          >
            <option value="ACTIVE">Active</option>
            <option value="DRAFT">Draft</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="OUT_OF_STOCK"> Out of Stock</option>
            <option value="DISCONTINUED">Discontinued</option>
          </select>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Brand</label>
            <input
              className="form-control"
              name="brand"
              value={form.brand}
              onChange={handleChange}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Model Number</label>
            <input
              className="form-control"
              name="modelNumber"
              value={form.modelNumber}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label">Condition</label>
          <select
            name="productCondition"
            value={form.productCondition}
            onChange={handleChange}
            className="form-select"
            required
          >
            {PRODUCT_CONDITIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Existing Images */}
        <div className="mb-4">
          <label className="form-label">Existing Images</label>
          <div className="d-flex gap-3 flex-wrap">
            {existingImages.map((img) => (
              <div key={img.id} className="position-relative text-center">
                <img
                  src={`${IMAGE_BASE}${img.storedName}`}
                  alt={img.originalName}
                  width={110}
                  height={110}
                  style={{ objectFit: "cover", borderRadius: 8 }}
                />

                <div className="mt-1 small text-muted">{img.originalName}</div>

                <div className="mt-1">
                  <input
                    type="radio"
                    name="mainImage"
                    checked={mainImage === img.id}
                    onChange={() => setMainImage(img.id)}
                  />{" "}
                  Main
                </div>

                <button
                  type="button"
                  className="btn btn-sm btn-danger position-absolute top-0 end-0"
                  onClick={() => handleRemoveExistingImage(img)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Add New Images */}
        <div className="mb-4">
          <label className="form-label">Add New Images</label>
          <input
            type="file"
            multiple
            accept="image/*"
            className="form-control"
            onChange={handleNewImages}
          />

          <div className="d-flex gap-3 mt-3 flex-wrap">
            {previewImages.map((img, index) => (
              <div key={index} className="position-relative">
                <img
                  src={img.url}
                  alt=""
                  width={110}
                  height={110}
                  style={{ objectFit: "cover", borderRadius: 8 }}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-danger position-absolute top-0 end-0"
                  onClick={() => handleRemoveNewImage(index)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/dashboard/catalog")}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
